/**
 * Composition primitives for the landing page.
 *
 * Everything here is layout and typography only — no product content — so the
 * section files read as a document outline rather than a pile of style objects.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Link, useRouter } from 'expo-router';

import AppStoreBadge from '@/components/Icons/AppStoreBadge';
import GooglePlayBadge from '@/components/Icons/GooglePlayBadge';
import MacAppButton from '@/components/Buttons/MacAppButton';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  openStoreUrl,
} from '@/components/PublicFooter/publicFooterStoreLinks';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';

import {
  ACCENT_BRIGHT,
  ACCENT_TINT,
  EASE,
  LAYOUT,
  ON_ACCENT,
  SERIF,
  accentAlpha,
  isWeb,
  displayType,
  labelType,
  landingBand,
  landingPalette,
  prefersReducedMotion,
  proseType,
  sectionSpace,
  sidePadding,
} from './landingTokens';

const STORES = [
  { key: 'ios', url: APP_STORE_URL, label: 'Download on the App Store', Badge: AppStoreBadge, width: 140 },
  { key: 'mac' },
  { key: 'play', url: GOOGLE_PLAY_URL, label: 'Get it on Google Play', Badge: GooglePlayBadge, width: 152 },
];

/** Everything a landing section needs to lay itself out. */
export function useLanding() {
  const { theme } = useTheme();
  const { isXS, isSM, windowWidth } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  return useMemo(() => {
    const width = windowWidth || LAYOUT.max;
    return {
      c: landingPalette(isDark),
      band: landingBand(isDark),
      isDark,
      isXS,
      isCompact: isXS || isSM,
      width,
      pad: sidePadding(width),
      space: sectionSpace(width),
      display: (opts) => displayType(width, opts),
      prose: (opts) => proseType(width, opts),
    };
  }, [isDark, isXS, isSM, windowWidth]);
}

/**
 * Flips to `true` one paint after mount so CSS transitions have an initial
 * state to animate away from. Returns `true` immediately when motion is
 * reduced or unsupported.
 */
export function useEntrance(enabled = true) {
  const skip = !isWeb || !enabled || prefersReducedMotion();
  const [entered, setEntered] = useState(skip);

  useEffect(() => {
    if (skip) {
      setEntered(true);
      return undefined;
    }
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [skip]);

  return entered;
}

/**
 * Hover and press state as plain booleans, plus the handlers that drive them.
 *
 * `Pressable`'s own `style={({ hovered }) => …}` callback is unusable on
 * anything rendered through `Link asChild`: Radix's Slot merges `style` with an
 * object spread, so a function arrives as `{}` and every declaration is
 * silently dropped. Tracking the states here keeps `style` a plain object.
 * Covered by `navigationAnchors.web.test.jsx`.
 */
export function useInteractive() {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const handlers = useMemo(() => ({
    onHoverIn: () => setHovered(true),
    onHoverOut: () => setHovered(false),
    onPressIn: () => setPressed(true),
    onPressOut: () => setPressed(false),
  }), []);

  return { hovered, pressed, handlers };
}

/** Fires once when the node first scrolls into view. */
export function useInView({ rootMargin = '0px 0px -12% 0px' } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(!isWeb);

  useEffect(() => {
    if (!isWeb) return undefined;
    if (
      typeof window === 'undefined'
      || !('IntersectionObserver' in window)
      || prefersReducedMotion()
    ) {
      setInView(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setInView(true);
        observer.disconnect();
      }
    }, { threshold: 0, rootMargin });
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, inView];
}

/* ------------------------------------------------------------------ */
/* Structure                                                           */
/* ------------------------------------------------------------------ */

/** Hairline divider. The page's primary structural device. */
export function Rule({ color, opacity = 1, style }) {
  const { c } = useLanding();
  return (
    <View
      aria-hidden
      style={[{ height: 1, width: '100%', backgroundColor: color || c.rule, opacity }, style]}
    />
  );
}

/** Full-bleed hairline that spans the viewport, ignoring the content measure. */
export function BleedRule({ color }) {
  const { c } = useLanding();
  return <View aria-hidden style={{ height: 1, width: '100%', alignSelf: 'stretch', backgroundColor: color || c.rule }} />;
}

/**
 * Centered content measure with fluid gutters.
 * `bleed` lets a section paint edge to edge while keeping its content aligned.
 */
export function Section({
  children,
  label,
  top = true,
  bottom = true,
  max = LAYOUT.max,
  background,
  style,
  contentStyle,
}) {
  const { pad, space } = useLanding();
  return (
    <View
      accessibilityRole={isWeb ? 'region' : undefined}
      accessibilityLabel={label}
      style={[
        {
          width: '100%',
          alignSelf: 'stretch',
          alignItems: 'center',
          backgroundColor: background,
          overflow: 'hidden',
        },
        style,
      ]}
    >
      <View
        style={[
          {
            width: '100%',
            maxWidth: max,
            paddingHorizontal: pad,
            paddingTop: top ? space : 0,
            paddingBottom: bottom ? space : 0,
          },
          contentStyle,
        ]}
      >
        {children}
      </View>
    </View>
  );
}

/** Two-column editorial split: statement on one side, evidence on the other. */
export function Split({ children, gap, align = 'flex-start', reverse = false, ratio = [1, 1] }) {
  const { isCompact, width } = useLanding();
  const resolvedGap = gap ?? (isCompact ? 40 : Math.round(width * 0.05));
  const items = React.Children.toArray(children);

  if (isCompact) {
    return <View style={{ gap: resolvedGap }}>{items}</View>;
  }

  return (
    <View style={{ flexDirection: reverse ? 'row-reverse' : 'row', gap: resolvedGap, alignItems: align }}>
      {items.map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <View key={`split-${index}`} style={{ flex: ratio[index] ?? 1, minWidth: 0 }}>
          {child}
        </View>
      ))}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/* Typography                                                          */
/* ------------------------------------------------------------------ */

/** Small caps label preceded by a short rule. `tone="band"` for use inside a band. */
export function Kicker({ children, tone, style }) {
  const { c, band } = useLanding();
  const color = tone === 'band' ? band.muted : c.muted;
  return (
    <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 12 }, style]}>
      <View style={{ width: 28, height: 1, backgroundColor: c.accent }} />
      <Text weight="bold" style={{ color, ...labelType(11, 0.18) }}>
        {children}
      </Text>
    </View>
  );
}

/**
 * Italic serif accent, used sparingly as the page's one typographic flourish.
 * Renders as a `span` so it stays valid phrasing content inside a heading.
 */
export function Accent({ children, size, leading, color }) {
  const { c } = useLanding();
  return (
    <Text
      tag="span"
      weight="bold"
      style={{
        fontFamily: SERIF,
        fontStyle: 'italic',
        color: color || c.accent,
        fontSize: size,
        lineHeight: leading,
        letterSpacing: size ? Math.round(size * -0.015 * 10) / 10 : undefined,
      }}
    >
      {children}
    </Text>
  );
}

/** Body copy at a comfortable measure. */
export function Lede({ children, tone, size = 'lg', style, max = LAYOUT.text }) {
  const { c, band, prose } = useLanding();
  const type = size === 'lg'
    ? prose({ min: 16.5, max: 19.5, leading: 1.58 })
    : prose({ min: 14.5, max: 16, leading: 1.6 });
  const color = tone === 'band' ? band.muted : c.muted;

  return (
    <Text tag="p" style={[{ color, maxWidth: max, ...type }, style]}>
      {children}
    </Text>
  );
}

/**
 * The page's display heading.
 *
 * `lines` is an array of per-line content, which each get their own clipping
 * mask so they can rise into place in sequence. The nesting is all `span`
 * elements with `display: block`, which keeps the markup valid phrasing content
 * inside the heading while still letting each line be its own block box.
 *
 * Transforms are CSS strings rather than React Native transform arrays because
 * `Text` with a `tag` renders a raw DOM element, which cannot interpret them.
 *
 * Per-line masking assumes each line actually fits on one line. On narrow
 * viewports lines re-wrap, so the whole heading falls back to a single
 * fade-rise instead.
 */
export function DisplayHeading({
  tag = 'h1',
  weight = 'bold',
  lines,
  entered = true,
  type,
  color,
  stagger = 110,
  delay = 0,
  mask = true,
  align,
  style,
}) {
  const { c, isXS } = useLanding();
  const ink = color || c.ink;
  const offset = Math.round((type?.lineHeight || 80) * 1.15);
  const useMask = isWeb && mask && !isXS && !prefersReducedMotion();

  const headingStyle = {
    color: ink,
    ...type,
    textAlign: align,
    ...(isWeb ? { display: 'block' } : {}),
    ...style,
  };

  /**
   * `Text` always emits its own size/weight/color defaults, which would
   * override the heading's on every nested span. Restate them per line.
   */
  const lineStyle = { color: ink, ...type, textAlign: align };

  if (!isWeb) {
    return (
      <Text tag={tag} weight={weight} style={headingStyle}>
        {lines.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`line-${index}`}>
            {index > 0 ? '\n' : null}
            {line}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  if (!useMask) {
    return (
      <Text
        tag={tag}
        weight={weight}
        style={{
          ...headingStyle,
          whiteSpace: 'pre-line',
          opacity: entered ? 1 : 0,
          transform: entered ? 'none' : 'translateY(16px)',
          transition: `opacity 760ms ${EASE} ${delay}ms, transform 760ms ${EASE} ${delay}ms`,
        }}
      >
        {lines.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`line-${index}`}>
            {index > 0 ? '\n' : null}
            {line}
          </React.Fragment>
        ))}
      </Text>
    );
  }

  return (
    <Text tag={tag} weight={weight} style={headingStyle}>
      {lines.map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`line-${index}`}>
          {/*
            Adjacent block spans concatenate with no separator, so the heading's
            text content — what a screen reader announces and what a search
            snippet quotes — would read "Gab AIcan do". Whitespace between two
            blocks collapses away, so this costs nothing visually.
          */}
          {index > 0 ? ' ' : null}
          <Text
            tag="span"
            weight={weight}
            style={{ ...lineStyle, display: 'block', overflow: 'hidden', paddingBottom: 10, marginBottom: -10 }}
          >
            <Text
              tag="span"
              weight={weight}
              style={{
                ...lineStyle,
                display: 'block',
                whiteSpace: 'nowrap',
                transform: entered ? 'translateY(0)' : `translateY(${offset}px)`,
                transition: `transform 1100ms ${EASE} ${delay + index * stagger}ms`,
              }}
            >
              {line}
            </Text>
          </Text>
        </React.Fragment>
      ))}
    </Text>
  );
}

/* ------------------------------------------------------------------ */
/* Controls                                                            */
/* ------------------------------------------------------------------ */

/** Solid accent CTA. The arrow advances on hover. */
export function PrimaryCta({ label, href = '/signup', onPress, tone, full = false, restrained = false }) {
  const router = useRouter();
  const { c, isDark } = useLanding();
  const { hovered, pressed, handlers } = useInteractive();
  const solid = tone === 'onDark' ? '#F6F8F3' : restrained ? (c.isDark ? '#E7ECE7' : '#17392B') : tone === 'invert' ? (isDark ? '#F3F6F3' : '#0F1512') : (hovered ? ACCENT_BRIGHT : c.accentFill);
  const fg = tone === 'onDark' ? '#143A2B' : restrained ? (c.isDark ? '#142218' : '#FFFFFF') : tone === 'invert' ? (isDark ? '#0F1512' : '#F7F9F7') : ON_ACCENT;
  const useLink = isWeb && href && !onPress;

  const button = (
    <Pressable
      {...handlers}
      onPress={useLink ? undefined : onPress || (() => router.push(href))}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={{
        height: 54,
        paddingHorizontal: 30,
        borderRadius: restrained ? 6 : 999,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        alignSelf: full ? 'stretch' : 'flex-start',
        backgroundColor: solid,
        opacity: pressed ? 0.9 : 1,
        transform: [{ translateY: isWeb && hovered && !pressed ? -2 : 0 }],
        ...(isWeb ? {
          cursor: 'pointer',
          transition: `transform 220ms ${EASE}, box-shadow 220ms ${EASE}, background-color 220ms ${EASE}, opacity 160ms linear`,
          boxShadow: restrained || tone === 'invert'
            ? 'none'
            : (hovered ? `0 16px 40px ${accentAlpha(0.38, isDark)}` : `0 8px 24px ${accentAlpha(0.24, isDark)}`),
        } : {}),
      }}
    >
      <Text weight="bold" style={{ color: fg, fontSize: 15.5, letterSpacing: -0.1 }}>{label}</Text>
      <View
        style={isWeb ? {
          transform: [{ translateX: hovered ? 4 : 0 }],
          transition: `transform 220ms ${EASE}`,
        } : null}
      >
        <Icon name="arrow-right" size={13} color={fg} />
      </View>
    </Pressable>
  );

  if (useLink) {
    return (
      <Link href={href} asChild>
        {button}
      </Link>
    );
  }

  return button;
}

/**
 * Mobile store badges, the Mac page link, and an optional browser extension.
 * The featured Mac badge gets its own larger row above the other platforms.
 * `exclude` drops platforms by key (`ios`, `mac`, `play`).
 */
export function StoreBadges({ scale = 1, exclude, style, featuredMac = false, showExtension = false }) {
  const { isXS } = useLanding();
  const factor = scale * (isXS ? 0.92 : 1);
  const stores = exclude?.length ? STORES.filter(({ key }) => !exclude.includes(key)) : STORES;
  const featureMac = featuredMac && stores.some(({ key }) => key === 'mac');
  const rowStores = featureMac ? stores.filter(({ key }) => key !== 'mac') : stores;

  return (
    <View style={[{ flexDirection: featureMac ? 'column' : 'row', flexWrap: 'wrap', alignItems: 'center', gap: featureMac ? 16 : 10 }, style]}>
      {featureMac && <MacAppButton featured scale={factor} />}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: featureMac ? 'center' : 'flex-start', gap: 10 }}>
      {rowStores.map(({ key, url, label, Badge, width }) => key === 'mac' ? (
        <MacAppButton key={key} scale={factor} />
      ) : (
        <Pressable
          key={key}
          onPress={() => openStoreUrl(url)}
          accessibilityRole="link"
          accessibilityLabel={label}
          style={({ pressed, hovered }) => ({
            opacity: pressed ? 0.75 : 1,
            transform: [{ scale: isWeb && hovered ? 1.03 : 1 }],
            ...(isWeb ? { cursor: 'pointer', transition: `transform 200ms ${EASE}` } : {}),
          })}
        >
          <Badge width={Math.round(width * factor)} />
        </Pressable>
      ))}
      {showExtension && <BrowserExtensionBadge scale={factor} />}
      </View>
    </View>
  );
}

function BrowserExtensionBadge({ scale }) {
  const { hovered, pressed, handlers } = useInteractive();
  return <Link href="/extension" asChild>
    <Pressable {...handlers} accessibilityRole="link" accessibilityLabel="Explore the Gab AI extension for Chrome and Brave"
      style={{ minHeight: Math.round(47 * scale), paddingHorizontal: 13, paddingVertical: 6, borderWidth: 1, borderColor: '#A6A6A6', borderRadius: 7, backgroundColor: '#000000', flexDirection: 'row', alignItems: 'center', gap: 9, opacity: hovered || pressed ? 0.82 : 1 }}>
      <Icon name="globe" size={Math.round(23 * scale)} color="#FFFFFF" />
      <View style={{ gap: 1 }}>
        <Text style={{ color: '#FFFFFF', fontSize: Math.round(10 * scale) }}>Browser extension</Text>
        <Text weight="medium" style={{ color: '#FFFFFF', fontSize: Math.round(16 * scale) }}>Chrome / Brave</Text>
      </View>
      <Icon name="arrow-right" size={10} color="#FFFFFF" />
    </Pressable>
  </Link>;
}

/**
 * Quiet text link whose underline draws in on hover. `tone="band"` follows the
 * band surface; `tone="onDark"` is for links that always sit on a dark card.
 */
export function GhostCta({ label, href, onPress, tone, icon = 'arrow-right', style }) {
  const router = useRouter();
  const { c, band } = useLanding();
  const { hovered, pressed, handlers } = useInteractive();
  const color = tone === 'band' ? band.ink : tone === 'onDark' ? '#F3F6F3' : c.ink;
  const rule = tone === 'band' ? band.rule
    : tone === 'onDark' ? 'rgba(243,246,243,0.26)' : c.ruleStrong;
  const hoverRule = tone === 'onDark' ? ACCENT_TINT : c.accent;
  const useLink = isWeb && href && !onPress;

  const linkButton = (
    <Pressable
      {...handlers}
      onPress={useLink ? undefined : onPress || (() => router.push(href))}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={StyleSheet.flatten([
        {
          height: 54,
          justifyContent: 'center',
          alignSelf: 'flex-start',
          opacity: pressed ? 0.7 : 1,
          ...(isWeb ? { cursor: 'pointer' } : {}),
        },
        style,
      ])}
    >
      <View style={{ gap: 5 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
          <Text weight="bold" style={{ color, fontSize: 15, letterSpacing: -0.1 }}>{label}</Text>
          <View
            style={isWeb ? {
              transform: [{ translateX: hovered ? 3 : 0 }],
              transition: `transform 220ms ${EASE}`,
            } : null}
          >
            <Icon name={icon} size={12} color={color} />
          </View>
        </View>
        <View
          style={{
            height: 1,
            backgroundColor: hovered ? hoverRule : rule,
            width: isWeb ? (hovered ? '100%' : '38%') : '100%',
            ...(isWeb ? { transition: `width 320ms ${EASE}, background-color 220ms linear` } : {}),
          }}
        />
      </View>
    </Pressable>
  );

  if (useLink) {
    return (
      <Link href={href} asChild>
        {linkButton}
      </Link>
    );
  }

  return linkButton;
}

/**
 * Puts a solid CTA and a ghost link on one row on desktop; stacks on XS.
 * Every marketing hero pairs the two this way.
 */
export function CtaRow({ children, style }) {
  const { isXS } = useLanding();
  return (
    <View
      style={[
        {
          flexDirection: isXS ? 'column' : 'row',
          alignItems: isXS ? 'stretch' : 'center',
          gap: isXS ? 6 : 24,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/**
 * Pill label. `tone="accent"` for routing/state chips inside product mocks,
 * `tone="band"` when sitting on a band surface, `tone="onDark"` for chips that
 * are always over a dark surface such as a code panel.
 */
export function Chip({ children, icon, tone = 'default', size = 'sm' }) {
  const { c, band } = useLanding();
  const isAccent = tone === 'accent';
  const isBand = tone === 'band';
  const isOnDark = tone === 'onDark';

  const bg = isAccent ? c.accentSoft
    : isBand ? band.surface
      : isOnDark ? 'rgba(243,246,243,0.06)' : c.panel;
  const border = isAccent ? c.accentRing
    : isBand ? band.rule
      : isOnDark ? 'rgba(243,246,243,0.14)' : c.rule;
  const fg = isAccent ? c.accent
    : isBand ? band.muted
      : isOnDark ? 'rgba(243,246,243,0.72)' : c.muted;

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: size === 'sm' ? 10 : 12,
        paddingVertical: size === 'sm' ? 5 : 7,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: border,
        backgroundColor: bg,
      }}
    >
      {icon ? <Icon name={icon} size={9} color={fg} solid /> : null}
      <Text weight="bold" style={{ color: fg, fontSize: 10.5, letterSpacing: 0.2 }}>{children}</Text>
    </View>
  );
}

/** Large editorial numeral with a caption. */
export function Figure({ value, label, tone, accent = false, size = 'lg' }) {
  const { c, band, display } = useLanding();
  const type = size === 'lg'
    ? display({ min: 40, max: 68, leading: 0.94, tracking: -0.045 })
    : display({ min: 30, max: 44, leading: 0.96, tracking: -0.04 });
  const ink = tone === 'band' ? band.ink : c.ink;
  const caption = tone === 'band' ? band.muted : c.muted;

  return (
    <View>
      <Text
        weight="bold"
        style={{ color: accent ? c.accent : ink, fontVariant: ['tabular-nums'], ...type }}
      >
        {value}
      </Text>
      <Text weight="medium" style={{ color: caption, marginTop: 10, fontSize: 12.5, letterSpacing: 0.1 }}>
        {label}
      </Text>
    </View>
  );
}

/**
 * Infinite horizontal ticker. The track holds two identical copies and CSS
 * translates it by exactly half its width, so the loop is seamless.
 * Animation lives in the page-level stylesheet, keyed off `data-landing-marquee`.
 */
export function Marquee({ items, tone, reverse = false }) {
  const { c, band, isXS } = useLanding();
  const color = tone === 'band' ? band.faint : c.faint;
  const fade = 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)';

  return (
    <View
      aria-hidden
      style={{
        width: '100%',
        alignSelf: 'stretch',
        overflow: 'hidden',
        flexDirection: 'row',
        ...(isWeb ? { maskImage: fade, WebkitMaskImage: fade } : {}),
      }}
    >
      <View
        {...(isWeb ? { dataSet: { landingMarquee: reverse ? 'reverse' : 'true' } } : {})}
        style={{ flexDirection: 'row', alignItems: 'center' }}
      >
        {[0, 1].map((copy) => (
          <View key={`copy-${copy}`} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: isXS ? 16 : 20 }}>
            {items.map((item) => (
              <View key={`${copy}-${item}`} style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Text weight="bold" style={{ color, marginHorizontal: isXS ? 16 : 24, ...labelType(isXS ? 10.5 : 11.5, 0.2) }}>
                  {item}
                </Text>
                <View style={{ width: 3, height: 3, borderRadius: 2, backgroundColor: c.accent, opacity: 0.7 }} />
              </View>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
}
